import { useLayoutEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { getGetCurrentUserQueryKey } from '@cdf/api-client-react';
import { useAuth } from '@/hooks/use-auth';
import { entityCacheIdentity } from '@/features/tenancy/context-navigation';

function newEntityClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false }
    }
  });
}

/** A route or account change gets a fresh data cache before entity pages render. */
export function EntityCacheBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { accountKey } = useAuth();
  const rootClient = useQueryClient();
  const identity = entityCacheIdentity(accountKey, pathname);
  const [scope, setScope] = useState(() => ({ identity, accountKey, client: newEntityClient() }));

  useLayoutEffect(() => {
    if (scope.identity === identity) return;
    void scope.client.cancelQueries();
    scope.client.clear();
    // The outer session observer owns entity-scoped /me refreshes. Removing
    // its active query here can strand it with pre-navigation capabilities.
    // Purge the previous account only; ordinary entity changes retain correctly
    // keyed permissions while the inner data cache remains strictly isolated.
    if (scope.accountKey !== accountKey) {
      rootClient.removeQueries({ queryKey: [...getGetCurrentUserQueryKey(), scope.accountKey] });
    }
    setScope({ identity, accountKey, client: newEntityClient() });
  }, [identity, accountKey, rootClient, scope]);

  if (scope.identity !== identity) return null;
  return <QueryClientProvider client={scope.client}>{children}</QueryClientProvider>;
}
